import { memo } from 'react';
import { IconGear } from '@optimizely/axiom-icons';
import { NodeProps } from '@xyflow/react';

import { NodeConnectionTypes, NodeTypeCategory } from '../../types/connections';
import { NODE_TYPE } from '../../types/node';
import { defineNode } from '../../utils/node-factory';
import { BaseNodeRenderer } from '../components/renderers/BaseNodeRenderer/BaseNodeRenderer';

/**
 * Configuration for the Nested Workflow node type.
 * Executes another workflow as a step in this workflow.
 */
export const nestedWorkflowConfig = defineNode({
  description: {
    displayName: 'Nested Workflow',
    name: NODE_TYPE.NESTED_WORKFLOW,
    description: 'Execute another workflow as a step in this workflow',
    category: NodeTypeCategory.UTILITY,
    iconInfo: {
      icon: IconGear,
      color: 'fg.accent.strong',
    },
    input: {
      type: NodeConnectionTypes.Main,
      filter: undefined,
    },
    output: {
      type: NodeConnectionTypes.Main,
      displayName: 'Output',
      maxConnections: 1,
    },
    properties: [
      {
        displayName: 'Name',
        name: 'name',
        type: 'string',
        required: true,
        default: '',
        description: 'A descriptive name for this workflow step',
      },
      {
        displayName: 'Description',
        name: 'description',
        type: 'string',
        required: false,
        default: '',
        description: 'Optional description of what this nested workflow does',
      },
      {
        displayName: 'Step ID',
        name: 'step_id',
        type: 'string',
        required: true,
        default: '',
        description: 'Unique identifier for this step in the workflow',
      },
      {
        displayName: 'Workflow ID',
        name: 'workflow_id',
        type: 'string',
        required: true,
        default: '',
        description: 'The ID of the workflow to execute as a nested step',
      },
      {
        displayName: 'Message',
        name: 'message',
        type: 'string',
        required: true,
        default: '',
        description: 'The message or input to pass to the nested workflow',
      },
      {
        displayName: 'Next Step ID',
        name: 'next_step_id',
        type: 'string',
        required: false,
        default: null,
        description: 'The ID of the step to execute after this nested workflow completes',
      },
      {
        displayName: 'Parameters',
        name: 'parameters',
        type: 'json',
        required: false,
        default: {},
        description: 'Additional parameters to pass to the nested workflow',
      },
      {
        displayName: 'Retry Strategy',
        name: 'retry_strategy',
        type: 'json',
        required: false,
        default: {},
        description: 'Configuration for retry behavior on failures',
      },
      {
        displayName: 'Timeout (seconds)',
        name: 'timeout_seconds',
        type: 'number',
        required: false,
        default: null,
        description: 'Maximum time to wait for nested workflow completion',
        typeOptions: {
          minValue: 1,
          maxValue: 10800, // 3 hours
        },
      },
    ],
    defaults: {
      name: 'Nested Workflow',
      parameters: {
        name: '',
        description: '',
        step_id: '',
        workflow_id: '',
        message: '',
        next_step_id: null,
        parameters: {},
        retry_strategy: {},
        timeout_seconds: null,
      },
    },
  },

  render: memo(function NestedWorkflowRenderer(props: NodeProps) {
    return <BaseNodeRenderer {...props} />;
  }),
});
